import React, { useLayoutEffect, useMemo } from 'react';
import { useFrame, useThree } from '@react-three/fiber';
import { EffectComposer, SMAA, ToneMapping, Bloom } from '@react-three/postprocessing';
import { ToneMappingMode } from 'postprocessing';
import * as THREE from 'three';
import { useAppSelector } from '../hooks/redux';
import { getFabVisualConfig } from '../utils/fabVisualConfig';
import {
  getCanvas3DSimulationDpr,
  getCanvas3DToneMapping,
  getCanvas3DToneMappingExposure,
  isClassicWebGLRenderer,
  isWindowsGpuHost,
} from '../utils/canvas3dRenderer';
import {
  FAB_TONE_MAPPING,
  getFabPostProcessingMultisampling,
  shouldUseSmaaFallback,
} from './fabPostProcessingConfig';

export type FabPostProcessingProps = {
  enabled?: boolean;
};

/**
 * Premium FAB post-processing pipeline:
 * - Controlled selective Bloom: Only status LEDs, beacon towers, and emissives glow gently without floor flaring.
 * - ACES Filmic Tone Mapping: Preserves crisp clean whites, specular highlights, and dark chassis details.
 * - Anti-Aliasing (SMAA / MSAA): Removes rail/equipment staircasing without blur.
 */
export function Canvas3DRenderBudget({ restingDpr }: { restingDpr: number }) {
  const gl = useThree((s) => s.gl);
  const setDpr = useThree((s) => s.setDpr);
  const simulationRunning = useAppSelector((s) => s.simulation.running);
  const classic = isClassicWebGLRenderer(gl as { isWebGLRenderer?: boolean });

  useLayoutEffect(() => {
    const composerOn =
      getFabVisualConfig().enablePostProcessing && classic && !simulationRunning;
    gl.toneMapping = composerOn ? THREE.NoToneMapping : getCanvas3DToneMapping();
    gl.toneMappingExposure = getCanvas3DToneMappingExposure();
  }, [classic, gl, simulationRunning]);

  useLayoutEffect(() => {
    if (!simulationRunning) setDpr(restingDpr);
  }, [restingDpr, setDpr, simulationRunning]);

  useFrame(() => {
    if (!simulationRunning) return;
    const next = getCanvas3DSimulationDpr(isWindowsGpuHost());
    if (Math.abs(gl.getPixelRatio() - next) > 0.01) setDpr(next);
  });

  return null;
}

export const FabPostProcessing: React.FC<FabPostProcessingProps> = ({ enabled = true }) => {
  const gl = useThree((s) => s.gl);
  const simulationRunning = useAppSelector((s) => s.simulation.running);
  const config = getFabVisualConfig();
  const windows = isWindowsGpuHost();
  const webgl = gl as THREE.WebGLRenderer;
  const active =
    enabled &&
    config.enablePostProcessing &&
    isClassicWebGLRenderer(gl as { isWebGLRenderer?: boolean }) &&
    Boolean(webgl?.state?.buffers) &&
    !simulationRunning;
  const hasDxfFloor = useAppSelector(
    (s) => s.floorplan.visible && s.floorplan.polylines.length > 0
  );
  const multisampling = getFabPostProcessingMultisampling(windows);
  const useSmaa = shouldUseSmaaFallback(multisampling) && !hasDxfFloor;

  const toneMappingProps = useMemo(
    () => ({
      minLuminance: FAB_TONE_MAPPING.minLuminance,
      middleGrey: FAB_TONE_MAPPING.middleGrey,
      mode: ToneMappingMode.ACES_FILMIC,
    }),
    []
  );

  if (!active) return null;

  return (
    <EffectComposer multisampling={multisampling} enableNormalPass={false} stencilBuffer={false}>
      <ToneMapping {...toneMappingProps} />
      <Bloom
        luminanceThreshold={0.98}
        luminanceSmoothing={0.06}
        intensity={0.32}
        mipmapBlur
      />
      {useSmaa && <SMAA />}
    </EffectComposer>
  );
};

export default FabPostProcessing;
